import React, {
  useState,
  useRef,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import { FlareContext } from "./flareContext";
import { FlareContextType } from "./types";
import {
  FuseArdServiceV2Client,
  GetFlareConfigCommand,
  GetFlareConfigCommandOutput,
  ListRagaAvailabilitiesCommand,
  ListRagaAvailabilitiesCommandOutput,
  RagaAvailability,
  FlareReleaseDomain,
} from "@amzn/fuse-ard-service-v2-client";
import { Ard } from "@amzn/fuse-accounting-central-datastore-client";
import { Auth } from "aws-amplify";
import { FUSE_ARD_SERVICE_V2_ENDPOINT } from "../../config/fuse-ard-service-v2-config";
import {
  ArdFlareComparison,
  compareArdWithFlareConfig,
} from "./flare-comparison-service";
import {
  CLIENT_CLEANUP_TIMEOUT_MS,
  CBOR_CONTENT_TYPE,
  AWS_REGION,
  ERROR_MESSAGES,
} from "./constants";

interface FlareContextProviderProps {
  children: ReactNode;
}

export const FlareContextProvider: React.FC<FlareContextProviderProps> = ({
  children,
}) => {
  const [flareConfig, setFlareConfig] =
    useState<GetFlareConfigCommandOutput | null>(null);
  const [ragaAvailabilities, setRagaAvailabilities] = useState<
    RagaAvailability[] | null
  >(null);
  const [lastUpdatedOn, setLastUpdatedOn] = useState<Date | undefined>();
  const [loadingRagaAvailabilities, setLoadingRagaAvailabilities] =
    useState<boolean>(true);
  const [loadingFlareConfig, setLoadingFlareConfig] = useState<boolean>(true);
  const [loadingComparison, setLoadingComparison] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [ardFlareComparison, setArdFlareComparison] =
    useState<ArdFlareComparison | null>(null);

  // Use ref to store client instance to avoid recreating it on every render
  const clientRef = useRef<FuseArdServiceV2Client | null>(null);
  const clientTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const clientPromiseRef = useRef<Promise<FuseArdServiceV2Client> | null>(null);

  const getFuseArdServiceV2Client =
    useCallback(async (): Promise<FuseArdServiceV2Client> => {
      // Clear any existing timeout
      if (clientTimeoutRef.current) {
        clearTimeout(clientTimeoutRef.current);
      }

      // Return existing client if available
      if (clientRef.current) {
        // Reset timeout for client cleanup
        clientTimeoutRef.current = setTimeout(() => {
          clientRef.current = null;
        }, CLIENT_CLEANUP_TIMEOUT_MS);
        return clientRef.current;
      }

      // Return in-flight promise if creation is already happening
      if (clientPromiseRef.current) {
        return clientPromiseRef.current;
      }

      // Create promise for new client
      clientPromiseRef.current = (async () => {
        const credentials = await Auth.currentCredentials();
        const client = new FuseArdServiceV2Client({
          endpoint: FUSE_ARD_SERVICE_V2_ENDPOINT,
          region: AWS_REGION,
          credentials: Auth.essentialCredentials(credentials),
        });

        // Add the Accept header for CBOR transformation
        client.middlewareStack.add(
          (next: any) => (args: any) => {
            args.request.headers.accept = CBOR_CONTENT_TYPE;
            return next(args);
          },
          { name: "accept-cbor-header", override: true, step: "build" },
        );

        clientRef.current = client;
        clientPromiseRef.current = null; // Clear the promise

        // Set timeout to clean up client after 30 minutes
        clientTimeoutRef.current = setTimeout(() => {
          clientRef.current = null;
        }, CLIENT_CLEANUP_TIMEOUT_MS);

        return client;
      })();

      return clientPromiseRef.current;
    }, []);

  const getFlareConfig = useCallback(
    async (
      domains: FlareReleaseDomain[],
      paths: string[],
    ): Promise<GetFlareConfigCommandOutput | null> => {
      setLoadingFlareConfig(true);
      setLoadingComparison(true);
      setFlareConfig(null);
      setError(null);

      try {
        if (domains.length === 0 || paths.length === 0) {
          throw new Error("Domains and paths must not be empty");
        }

        const client = await getFuseArdServiceV2Client();
        const command = new GetFlareConfigCommand({
          domains,
          paths,
        });
        const response = await client.send(command);
        setFlareConfig(response);
        return response;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        setError(errorMessage);
        console.error("Error fetching Flare config:", err);
        return null;
      } finally {
        setLoadingFlareConfig(false);
      }
    },
    [getFuseArdServiceV2Client],
  );

  const listRagaAvailabilities = useCallback(
    async (
      forceFetchFromFlare: boolean = false,
    ): Promise<ListRagaAvailabilitiesCommandOutput | null> => {
      setLoadingRagaAvailabilities(true);
      setError(null);

      try {
        const client = await getFuseArdServiceV2Client();
        const command = new ListRagaAvailabilitiesCommand({
          forceFetchFromFlare,
        });
        const response = await client.send(command);

        if (!response.ragaAvailabilities) {
          throw new Error(ERROR_MESSAGES.MALFORMED_RESPONSE);
        }

        setRagaAvailabilities(response.ragaAvailabilities);
        setLastUpdatedOn(response.lastUpdatedOn);
        return response;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        setError(errorMessage);
        console.error("Error fetching Raga availabilities:", err);
        return null;
      } finally {
        setLoadingRagaAvailabilities(false);
      }
    },
    [getFuseArdServiceV2Client],
  );

  const compareArdWithFlare = useCallback(
    (ard: Ard | null): void => {
      setArdFlareComparison(null); // Clear old comparison data immediately
      setError(null);

      if (!ard) {
        return;
      }
      setLoadingComparison(true);

      try {
        const comparison = compareArdWithFlareConfig(ard, flareConfig);

        setArdFlareComparison(comparison);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        setError(errorMessage);
        console.error("Error comparing ARD with Flare config:", err);
      } finally {
        if (flareConfig) {
          setLoadingComparison(false);
        }
      }
    },
    [flareConfig],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (clientTimeoutRef.current) {
        clearTimeout(clientTimeoutRef.current);
      }
      clientRef.current = null;
    };
  }, []);

  const contextValue = useMemo<FlareContextType>(
    () => ({
      // State
      flareConfig,
      ragaAvailabilities,
      lastUpdatedOn,
      loadingRagaAvailabilities,
      loadingFlareConfig,
      loadingComparison,
      error,
      ardFlareComparison,
      // Actions
      getFlareConfig,
      listRagaAvailabilities,
      compareArdWithFlare,
      clearError,
    }),
    [
      flareConfig,
      ragaAvailabilities,
      lastUpdatedOn,
      loadingRagaAvailabilities,
      loadingFlareConfig,
      loadingComparison,
      error,
      ardFlareComparison,
      getFlareConfig,
      listRagaAvailabilities,
      compareArdWithFlare,
      clearError,
    ],
  );

  return (
    <FlareContext.Provider value={contextValue}>
      {children}
    </FlareContext.Provider>
  );
};
